import { useEditor } from "../store.js";
import { TEMPLATES } from "../templates/index.js";

export function TemplateGallery() {
  const isOpen = useEditor((s) => s.isGalleryOpen);
  const close = useEditor((s) => s.closeGallery);
  const setPage = useEditor((s) => s.setPage);

  if (!isOpen) return null;

  const onPick = (id: string) => {
    const tpl = TEMPLATES.find((t) => t.id === id);
    if (!tpl) return;
    // Deep-clone so editing doesn't mutate the template constant.
    setPage(structuredClone(tpl.page));
    close();
  };

  return (
    <div
      className="emke-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Template gallery"
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div className="emke-modal">
        <header className="emke-modal-header">
          <div>
            <div
              style={{
                fontSize: 11,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "var(--fg-dim)",
              }}
            >
              Templates
            </div>
            <h2 style={{ margin: "4px 0 0 0", fontSize: 18 }}>Pick a starting point</h2>
          </div>
          <button
            type="button"
            className="emke-btn"
            onClick={close}
            aria-label="Close template gallery"
          >
            ✕
          </button>
        </header>

        <div className="emke-modal-body">
          <div className="emke-template-grid">
            {TEMPLATES.map((t) => (
              <button
                key={t.id}
                type="button"
                className="emke-template-card"
                onClick={() => onPick(t.id)}
              >
                <div className="emke-template-preview">
                  <TemplatePreview templateId={t.id} />
                </div>
                <div className="emke-template-meta">
                  <div className="emke-template-name">{t.name}</div>
                  <div className="emke-template-desc">{t.description}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        <footer className="emke-modal-footer">
          <span style={{ color: "var(--fg-dim)", fontSize: 12 }}>
            Loading a template replaces your current page. Undo (Ctrl+Z) brings it back.
          </span>
        </footer>
      </div>
    </div>
  );
}

/**
 * Tiny abstract SVG preview per template. Hand-tuned — no real render.
 * Keeps the modal fast and offline-safe without bundling real screenshots.
 */
function TemplatePreview({ templateId }: { templateId: string }) {
  const common = {
    width: "100%",
    height: "100%",
    viewBox: "0 0 160 100",
    preserveAspectRatio: "none",
  } as const;

  switch (templateId) {
    case "blank":
      return (
        <svg {...common}>
          <rect width="160" height="100" fill="#ffffff" />
        </svg>
      );
    case "hero-cta":
      return (
        <svg {...common}>
          <rect width="160" height="100" fill="#0a0a0a" />
          <rect x="30" y="30" width="100" height="8" rx="2" fill="#fafafa" />
          <rect x="40" y="44" width="80" height="4" rx="1.5" fill="#525252" />
          <rect x="40" y="52" width="80" height="4" rx="1.5" fill="#525252" />
          <rect x="64" y="68" width="32" height="10" rx="3" fill="#a855f7" />
        </svg>
      );
    case "announcement":
      return (
        <svg {...common}>
          <rect width="160" height="100" fill="#fef3c7" />
          <rect x="50" y="14" width="60" height="3" rx="1" fill="#92400e" />
          <rect x="30" y="22" width="100" height="6" rx="2" fill="#1c1917" />
          <rect x="20" y="36" width="120" height="40" rx="4" fill="#fde68a" />
          <rect x="60" y="84" width="40" height="8" rx="2" fill="#92400e" />
        </svg>
      );
    case "coming-soon":
      return (
        <svg {...common}>
          <rect width="160" height="100" fill="#0a0a0a" />
          <rect x="20" y="42" width="120" height="8" rx="2" fill="#fafafa" />
          <rect x="40" y="56" width="80" height="3" rx="1" fill="#525252" />
          <rect x="60" y="74" width="40" height="8" rx="2" fill="#a855f7" />
        </svg>
      );
    case "feature-grid":
      return (
        <svg {...common}>
          <rect width="160" height="100" fill="#ffffff" />
          <rect x="40" y="16" width="80" height="6" rx="2" fill="#0a0a0a" />
          <rect x="50" y="26" width="60" height="3" rx="1" fill="#737373" />
          <rect x="12" y="44" width="40" height="46" rx="4" fill="#f5f5f5" />
          <rect x="60" y="44" width="40" height="46" rx="4" fill="#f5f5f5" />
          <rect x="108" y="44" width="40" height="46" rx="4" fill="#f5f5f5" />
        </svg>
      );
    case "about":
      return (
        <svg {...common}>
          <rect width="160" height="100" fill="#ffffff" />
          <rect x="16" y="12" width="24" height="3" rx="1" fill="#a855f7" />
          <rect x="16" y="18" width="80" height="7" rx="2" fill="#0a0a0a" />
          <rect x="16" y="32" width="128" height="22" rx="4" fill="#e5e5e5" />
          <rect x="16" y="62" width="50" height="4" rx="1" fill="#0a0a0a" />
          <rect x="16" y="70" width="120" height="3" rx="1" fill="#737373" />
          <rect x="16" y="76" width="100" height="3" rx="1" fill="#737373" />
        </svg>
      );
    case "split-hero":
      return (
        <svg {...common}>
          <rect width="160" height="100" fill="#ffffff" />
          <rect x="10" y="20" width="16" height="2" rx="1" fill="#a855f7" />
          <rect x="10" y="26" width="60" height="6" rx="2" fill="#0a0a0a" />
          <rect x="10" y="38" width="60" height="3" rx="1" fill="#737373" />
          <rect x="10" y="44" width="54" height="3" rx="1" fill="#737373" />
          <rect x="10" y="56" width="28" height="8" rx="2" fill="#a855f7" />
          <rect x="80" y="14" width="70" height="72" rx="6" fill="#e5e5e5" />
        </svg>
      );
    case "pricing":
      return (
        <svg {...common}>
          <rect width="160" height="100" fill="#ffffff" />
          <rect x="38" y="12" width="84" height="5" rx="2" fill="#0a0a0a" />
          <rect x="50" y="20" width="60" height="3" rx="1" fill="#737373" />
          <rect x="10" y="30" width="44" height="60" rx="5" fill="#f5f5f5" />
          <rect
            x="58"
            y="30"
            width="44"
            height="60"
            rx="5"
            fill="#faf5ff"
            stroke="#a855f7"
            strokeWidth="1"
          />
          <rect x="106" y="30" width="44" height="60" rx="5" fill="#f5f5f5" />
        </svg>
      );
    case "testimonial-grid":
      return (
        <svg {...common}>
          <rect width="160" height="100" fill="#ffffff" />
          <rect x="42" y="14" width="76" height="5" rx="2" fill="#0a0a0a" />
          <rect x="10" y="30" width="44" height="60" rx="5" fill="#f5f5f5" />
          <rect x="58" y="30" width="44" height="60" rx="5" fill="#f5f5f5" />
          <rect x="106" y="30" width="44" height="60" rx="5" fill="#f5f5f5" />
          <rect x="16" y="40" width="32" height="3" rx="1" fill="#525252" />
          <rect x="64" y="40" width="32" height="3" rx="1" fill="#525252" />
          <rect x="112" y="40" width="32" height="3" rx="1" fill="#525252" />
        </svg>
      );
    case "newsletter":
      return (
        <svg {...common}>
          <rect width="160" height="100" fill="#f5f3ff" />
          <rect x="50" y="20" width="60" height="3" rx="1" fill="#7c3aed" />
          <rect x="20" y="32" width="120" height="8" rx="2" fill="#0a0a0a" />
          <rect x="40" y="48" width="80" height="3" rx="1" fill="#525252" />
          <rect x="64" y="64" width="32" height="10" rx="3" fill="#7c3aed" />
          <rect x="54" y="84" width="52" height="2" rx="1" fill="#737373" />
        </svg>
      );
    default:
      return null;
  }
}
